// FE-PINV-067 — <CadenceHealthEdge> shared cadence-health stripe.
//
// One component, three views consume it (Grid cards / Kanban cards /
// List rows). Centralizes the palette so the three surfaces stay
// visually consistent and so the palette never gets re-typed across
// the codebase. The tokens come straight from FE-SCRM-008's existing
// ``cadenceColors`` map — no new tokens introduced.
//
//   <CadenceHealthEdge state="critical" variant="card" />  // 4px stripe
//   <CadenceHealthEdge state="behind"   variant="row"  />  // 4px rail
//
// The component is purely presentational; the parent card or row is
// expected to be `position: relative` so the absolutely-positioned
// stripe pins to its left edge.

import type { CSSProperties } from "react";


export type CadenceState =
  | "critical"
  | "behind"
  | "on_track"
  | "ahead"
  | "not_yet_urgent";


/** Tailwind background-color tokens mirroring FE-SCRM-008's
 *  ``CADENCE_BG_DOT`` map — reusing the same literals so a future
 *  palette tweak lands in one place. */
export const CADENCE_EDGE_TOKEN: Record<CadenceState, string> = {
  critical: "bg-ember",
  behind: "bg-amber",
  on_track: "bg-sage",
  ahead: "bg-teal",
  not_yet_urgent: "bg-border-subtle",
};


export interface CadenceHealthEdgeProps {
  state: CadenceState;
  /** ``card`` = absolutely-positioned 4px stripe at the parent card's
   *  left edge. ``row`` = 4px wide full-height rail at a list row's
   *  left edge. */
  variant: "card" | "row";
  /** Optional explicit height override. Honored by the ``row``
   *  variant when a list row needs to opt out of full-height (e.g.
   *  the bulk-action selection bar overlay). */
  height?: string;
  className?: string;
}


export function CadenceHealthEdge({
  state, variant, height, className,
}: CadenceHealthEdgeProps): JSX.Element {
  const token = CADENCE_EDGE_TOKEN[state];
  const variantClasses = variant === "card"
    ? "absolute left-0 top-0 bottom-0 w-1 rounded-l-md"
    : "absolute left-0 top-0 w-1 h-full";
  const style: CSSProperties | undefined =
    height !== undefined ? { height } : undefined;

  return (
    <span
      aria-label={`Cadence: ${state}`}
      role="presentation"
      data-testid={`cadence-edge-${state}`}
      className={[token, variantClasses, className ?? ""].join(" ").trim()}
      style={style}
    />
  );
}
